import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

const badgeVariants = cva(
	"inline-flex items-center px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
	{
		variants: {
			variant: {
				default: "rounded-md border border-transparent bg-slate-900 text-slate-50",
				secondary: "rounded-md border border-transparent bg-slate-100 text-slate-900",
				destructive: "rounded-md border border-transparent bg-red-500 text-slate-50",
				outline: "rounded-md border border-slate-200 text-slate-900",
				subtle: "rounded-full ring-1 ring-inset",
				strong: "rounded-full",
				pill: "rounded-full ring-1 ring-inset",
				tag: "rounded-sm border border-transparent",
			},
			tone: {
				neutral: "",
				success: "",
				warn: "",
				error: "",
				info: "",
			},
		},
		compoundVariants: [
			{
				variant: "subtle",
				tone: "neutral",
				className: "bg-slate-100 text-slate-700 ring-slate-200",
			},
			{
				variant: "subtle",
				tone: "success",
				className: "bg-green-100 text-green-700 ring-green-200",
			},
			{ variant: "subtle", tone: "warn", className: "bg-amber-100 text-amber-800 ring-amber-200" },
			{ variant: "subtle", tone: "error", className: "bg-red-100 text-red-700 ring-red-200" },
			{ variant: "subtle", tone: "info", className: "bg-blue-100 text-blue-700 ring-blue-200" },
			{ variant: "strong", tone: "neutral", className: "bg-slate-700 text-white" },
			{ variant: "strong", tone: "success", className: "bg-green-700 text-white" },
			{ variant: "strong", tone: "warn", className: "bg-amber-600 text-white" },
			{ variant: "strong", tone: "error", className: "bg-red-700 text-white" },
			{ variant: "strong", tone: "info", className: "bg-blue-700 text-white" },
			{ variant: "pill", tone: "neutral", className: "bg-slate-100 text-slate-700 ring-slate-200" },
			{ variant: "pill", tone: "success", className: "bg-green-100 text-green-700 ring-green-200" },
			{ variant: "pill", tone: "warn", className: "bg-amber-100 text-amber-800 ring-amber-200" },
			{ variant: "pill", tone: "error", className: "bg-red-100 text-red-700 ring-red-200" },
			{ variant: "pill", tone: "info", className: "bg-blue-100 text-blue-700 ring-blue-200" },
			{ variant: "tag", tone: "neutral", className: "bg-slate-100 text-slate-700" },
			{ variant: "tag", tone: "success", className: "bg-green-100 text-green-700" },
			{ variant: "tag", tone: "warn", className: "bg-amber-100 text-amber-800" },
			{ variant: "tag", tone: "error", className: "bg-red-100 text-red-700" },
			{ variant: "tag", tone: "info", className: "bg-blue-100 text-blue-700" },
		],
		defaultVariants: {
			variant: "default",
			tone: "neutral",
		},
	},
);

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, tone, ...props }: BadgeProps) {
	return <div className={cn(badgeVariants({ variant, tone }), className)} {...props} />;
}

export { Badge, badgeVariants };
